// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerStore.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { action, observe, computed, observable } from 'mobx';
import { map, filter, isNil, isArray, includes, getOr, get, omit, flow, find, isEmpty, placeholder } from 'lodash/fp';
import Constants from './data/Izenda.ReportDesignerConstants';
import fetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import reportsFetcher from 'fetchers/Izenda.ReportsDataFetcher';
import accessFetcher from 'fetchers/Izenda.AccessDataFetcher';
import ReportDesigner from './models/Izenda.ReportDesigner';
import AlertDetails from './models/Izenda.AlertDetails';
import uiState from 'logic/Izenda.UiState.js';
import AppGlobal from 'Izenda.AppGlobal';
import AppStore from 'logic/Izenda.AppStore';
import exportFetcher from 'fetchers/Izenda.ExportFetcher';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';
import { PERMISSION_KEY } from 'logic/data/Izenda.AuthorizationConstants';

// Child store
import FieldsStore from './Izenda.ReportDesignerFieldsStore';
import DataSourceStore from './Izenda.ReportDesignerDataSourceStore';
import ScheduleStore from './Izenda.ReportDesignerScheduleStore';
import AccessStore from 'logic/Izenda.AccessStore';
import localizer from 'share/localization/Izenda.Localizer';

/**
 * [ReportDesignerStore master store for report desginer]
 */
class ReportDesignerStore {
  @observable model;
  @observable reportAccessPriority = null;
  @observable alertDetails = new AlertDetails();
  @observable saveAsMode = false;
  @observable disabled = false;
  @observable isNewReport;
  @observable disableReportDesigner = false;
  @observable advancedMode = false;
  @observable isCompleteLoad;
  @observable globalReportName = null;
  @observable localReportName = null;
  @observable isShowingHeaderFooter = !AppGlobal.doesHideReportHeaderAndFooter();
  // Control to whether load draft or not.
  isLoadDraft: boolean = false;

  constructor(viewMode: any, reportId: any, cachedData: any, isReload: boolean) {
    this.switchToDesignReportContainer = false;
    this.disposers = [];
    this.model = cachedData ? cachedData : new ReportDesigner(viewMode, reportId, isReload);
    this.canSaveAs = !isNil(reportId);
    this.isNewReport = !reportId;
    this.isCompleteLoad = !!cachedData;
    this.fieldsStore = new FieldsStore(this, viewMode);
    this.dataSourceStore = new DataSourceStore(this);
    this.scheduleStore = new ScheduleStore(this);
    this.accessStore = new AccessStore(this.model.reportAccess, this.reportId);
    this.reportListURI = null;

    this.reactiveStore(this);
  }

  @computed
  get isEnableOverlay() {
    return this.isNewReport && !this.model.hasSelectedDataSource;
  }

  @computed
  get canSaveGlobalReport() {
    if (this.model.isGlobal) {
      return isNil(uiState.tenantId) && uiState.currentUser.systemAdmin;
    }
    return true;
  }

  @computed
  get reportId() {
    return this.model.reportId;
  }

  @computed
  get disableSubCategory() {
    return isEmpty(this.model.categoryId) || isNil(this.model.categoryId);
  }

  @computed
  get disableReportType() {
    return isEmpty(this.model.reportFields.reportFilter.filterFields) && this.model.reportFields.reportBody.hasAnyField;
  }

  @computed
  get statusSaveInto() {
    return {
      visible: isNil(uiState.tenantId) && !!uiState.currentUser.systemAdmin
    };
  }

  @computed
  get showCascadingChangedMessage() {
    return (
      this.fieldsStore.reportFilters.hasCascadingChanged && this.fieldsStore.reportFilters.showCascadingChangedMessage
    );
  }

  @computed
  get needUpdateResult() {
    return get('reportFilters.needUpdateResult', this.fieldsStore);
  }

  @computed
  get statusGlobalCheckbox() {
    let visible;
    let disabled = true;
    const checked = this.model.isGlobal;
    if (this.isNewReport) {
      visible = false;
      if (uiState.currentUser.systemAdmin && isNil(uiState.tenantId)) {
        visible = true;
        disabled = false;
      }
    } else {
      if (this.model.isGlobal) {
        visible = true;
        if (
          (uiState.currentUser.isSystemUser && !uiState.currentUser.systemAdmin && !isNil(uiState.tenantId)) ||
          (!uiState.currentUser.isSystemUser && isNil(uiState.tenantId))
        ) {
          visible = false;
        }
      } else {
        visible = false;
        if (uiState.currentUser.systemAdmin && isNil(uiState.tenantId)) {
          visible = true;
        }
      }
    }
    return {
      visible,
      disabled,
      checked
    };
  }

  @computed
  get previewRecord() {
    return this.model.previewRecord;
  }

  reactiveStore(store: any) {
    const reloadDisposer = observe(store, 'reportId', function() {
      store.loadReportDesigner();
    });
    store.disposers.push(reloadDisposer);
  }

  /**
   * To validate report name on API
   * @param  {object} data [report designer model]
   * @return {Promise}      Result form API
   */
  validateReportName(data: any) {
    // call validate on server
    return fetcher.validateReportName(data);
  }

  /**
   * To get the name text "Global/ Local" for category type
   */
  loadCategorySettings() {
    AppStore.loadCategorySettings()
      .bind(this)
      .then(
        action(result => {
          const globalReportType = find({ isGlobal: true }, result);
          const localReportType = find({ isGlobal: false }, result);
          this.globalReportName = getOr(null, 'name', globalReportType);
          this.localReportName = getOr(null, 'name', localReportType);
        })
      );
  }

  /**
   * Detect physicial change before save/save draft
   */
  @action
  detectReportChange(section: any) {
    const data = this.model.getReportDefinition(section);
    if (this.model.isGlobal && !isNil(this.accessStore.tenantId)) {
      data.category = undefined;
      data.categoryId = undefined;
      data.report.category = undefined;
      data.report.categoryId = undefined;
    }
    // only get reportDataSource in first loading time when we have no data
    data.report.reportDataSource = !isEmpty(data.report.reportDataSource)
      ? data.report.reportDataSource
      : this.model.reportDataSource;

    return fetcher.detectReportChange(data);
  }

  /**
   * Save Report Designer
   * @param {string} section [section of report designer need save]
   * * @param {string} section [section of report designer need save]
   * @return {promise} [promise result from API]
   */
  @action
  saveReport(section: any, saveFromViewer: any) {
    return this.saveOrSaveDraftReport(section, false, saveFromViewer);
  }

  /**
   * Save draft when switch page on Report Designer
   * @param {string} section [section of report designer need save]
   * @return {promise} [promise result from API]
   */
  @action
  saveDraftReport(section: any, keepReportBodyStates: any) {
    return this.saveOrSaveDraftReport(section, true /* is draft*/, false, keepReportBodyStates);
  }

  /**
   * Save Report Designer
   * @param {string} section [section of report designer need save]
   * @return {promise} [promise result from API]
   */
  @action
  saveOrSaveDraftReport(section: any, isSaveDraft: boolean, saveFromViewer: any, keepReportBodyStates: any) {
    !isSaveDraft && this.model.removeUnuseDynamicThresholds();
    const data = this.model.getReportDefinition(section);
    const saveFunction = isSaveDraft ? fetcher.saveDaftReportDesigner : fetcher.saveReportDesigner;
    // When to call save or save draft, it will keep this status for load data.
    this.isLoadDraft = isSaveDraft;

    this.alertDetails.reset();
    this.model.updateErrorMessages(null);

    return this.checkDataObjectLimitation().then(
      function(result) {
        if (result.success || saveFromViewer) {
          return saveFunction(data).then(
            action(
              function updateReportDesigner(result) {
                if (result.report) {
                  isSaveDraft && (result.report.version = data.report.version);

                  if (!isNil(getOr(null, 'report.reportFilter.filterFields', result))) {
                    result.report.reportFilter.filterFields
                      .filter(item => item.isParameter && !isNil(item.value) && !isEmpty(item.value))
                      .forEach(item => {
                        item.value = item.value.replace(
                          '[Current User]',
                          '[User: ' + uiState.currentUser.userName + ']'
                        );
                        if (!uiState.currentUser.systemAdmin) {
                          item.value = item.value.replace(
                            '[Current Tenant]',
                            '[Tenant: ' + uiState.currentUser.tenantDisplayID + ']'
                          );
                        }
                      });
                  }

                  const isDirty = this.model.checkDirty();
                  const isAdvancedMode = this.model.dataSource.reportMode === Constants.ADVANCED;
                  this.model.updateReportInfo(
                    result.reportKey.key,
                    result.report,
                    isAdvancedMode,
                    keepReportBodyStates
                  );
                  this.accessStore.updateReporId(result.report.id);
                  this.updateSchedules(result.report.id, isSaveDraft);

                  // TODO: should we switch to this.model.setIgnoreCheckChange(true) ?
                  this.ignoreCheckChange = true;

                  // only do this when saving, not save draft
                  if (!isSaveDraft) {
                    this.fieldsStore.getFilterData();
                    this.alertDetails.set({
                      success: true,
                      message: localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
                    });
                    this.canSaveAs = true;
                    this.accessStore.model.cleanDraftAccesses();
                    this.model.cleanDirty();
                  } else {
                    !isDirty && this.model.cleanDirty();
                  }
                  // Reset data source grid dirty form for breaking cycle relationships
                  this.dataSourceStore.resetDataSourceGridDirty();
                }

                if (!isEmpty(result.messages)) {
                  this.model.updateErrorMessages(result.messages);
                } else {
                  this.model.resetSaveAs();
                  this.scheduleStore.subscribedInstancesStore.model.cleanDirty();
                }

                // Get operator source value after save
                const configFilterField = get('reportFilters.configField', this.fieldsStore);
                this.fieldsStore.getOperatorValueSource(configFilterField);

                return {
                  success: isEmpty(result.messages),
                  messages: result.messages,
                  reportId: result.reportKey && result.reportKey.key
                };
              }.bind(this)
            )
          );
        }
        return result;
      }.bind(this)
    );
  }

  /**
   * Update schedules
   * @param {String} report id
   * @param {Boolean} save draft
   */
  @action
  updateSchedules(reportId: any, isSaveDraft: any) {
    if (!isSaveDraft) {
      this.model.schedules = [];
      const subscribedInstancesStore = this.scheduleStore.subscribedInstancesStore;
      subscribedInstancesStore.updateReportId(reportId);
      subscribedInstancesStore.loadSchedules(true, null);
    }
  }

  /**
   * validate relationships syntax
   * @param {string} section [section of report designer need validate]
   * @return {promise} [promise result from API]
   */
  @action
  validateRelationshipSyntax(section: any) {
    const data = this.model.getReportDefinition(section);

    return fetcher.validateRelationshipSyntax(data);
  }

  /**
   * validate simple mode relationships
   * @param {string} section [section of report designer need validate]
   * @return {promise} [promise result from API]
   */
  @action
  validateSimpleModeRelationship(section: any) {
    const data = this.model.getReportDefinition(section);

    return fetcher.validateSimpleModeRelationship(data);
  }

  /**
   * Cancel all the changes by getting the origin report key
   * @returns {*|Promise.<TResult>} [promise result from API]
   */
  @action
  cancelChanges() {
    const model = this.model;

    model.cleanDirty();
    return fetcher.cancelReportChanges(model.reportId);
  }

  /**
   *  Handle when change report type, reload list category and sub-category
   * @param {string} value [new report type]
   * @return {Promise} [promise result from API]
   */
  @action
  changeType(value: any) {
    this.model.updateType(value);
    return this.loadCategories(value);
  }

  /**
   *  Handle when change global type in SaveAs popup, reload list category and sub-category
   * @param {string} value
   * @return {Promise} [promise result from API]
   */
  @action
  changeSaveInto(value: any) {
    this.model.changeGlobalType(value);
    return this.loadCategories(this.model.reportType, null, value);
  }

  /**
   *  Handle when change report's category, reload list sub-category
   * @param {GUI|string} id [new category id]
   * @param {string} name [new category name]
   * @return {promise} [promise result from API]
   */
  @action
  changeCategory(id: any, name: any) {
    this.model.updateCategory(id, name);
    this.model.updateSubCategory();
    return this.loadSubCategories(id);
  }

  /**
   *  Handle when change report's category, reload list sub-category
   * @param {int} type
   * @param {string} tenantId
   * @param {Boolean} isGlobal
   * @return {promise} [promise result from API]
   */
  @action
  loadCategories(type: any, tenantId: any, isGlobal: any) {
    const model = this.model;
    const reportType = type || model.reportType;
    const checkIsGlobal = isNil(isGlobal) ? model.isGlobal : isGlobal;
    const fetchTenantId = isNil(tenantId) || !String.isGuidFormat(tenantId) ? uiState.getTenantId() : tenantId;

    return fetcher.loadCategories(reportType, fetchTenantId, checkIsGlobal).then(model.readCategoryData.bind(model));
  }

  /**
   *  Load list of sub category with teamplate and tenant from API
   */
  @action
  loadSubCategories(categoryId: any) {
    const model = this.model;
    const isValidCategoryId = !isNil(categoryId) && String.isGuidFormat(categoryId);

    return isValidCategoryId
      ? fetcher.loadSubCategories(categoryId).then(model.readSubCategoryData.bind(model))
      : Promise.resolve(model.readSubCategoryData());
  }

  /**
   *  Handle validate report name
   */
  @action
  validate(reportName: any) {
    const data = this.model.getGeneralData();
    // Set tenant Id
    const tenantId = uiState.getTenantId();
    tenantId && (data.tenantId = tenantId);
    // Assign the new report name for validate
    reportName && (data.name = reportName);

    // clear reportId if in save as mode
    if (this.saveAsMode) {
      data.id = null;
      this.saveAsMode = false;
    }

    return this.model.reportName ? this.validateReportName(data).then(get('messages')) : Promise.resolve('');
  }

  /**
   *  Set Alert Detail
   */
  @action
  setAlertDetails(data: any) {
    this.alertDetails.set(data);
  }

  /**
   *  Reset alert
   */
  @action
  resetAlertDetails() {
    this.alertDetails.reset();
  }

  /**
   * Change global report type
   * @param {int} value
   */
  @action
  changeGlobalType(value: any) {
    this.model.changeGlobalType(value);
  }

  /**
   * Clear category
   */
  @action
  clearCategory() {
    this.model.clearCategory();
  }

  /**
   * Clear sub-category
   */
  @action
  clearSubCategory() {
    this.model.clearSubCategory();
  }

  /**
   *  Get bundle from constants file
   */
  getBundle() {
    return Constants.BUNDLE;
  }

  /**
   *  Support add new report part from Toolbar
   */
  @action
  addNewReportPart() {
    this.model.reportFields.reportBody.addReportPart();
  }

  /**
   * compare selected items with data object limitation from db
   * @returns {boolean} result
   */
  checkDataObjectLimitation(isValid: any) {
    if (!isNil(isValid) && !isValid) {
      return Promise.resolve({ success: false });
    }
    const model = this.model;
    const defaultSelectedDataSourcesCount = 0;

    // only get reportDataSource in first loading time when we have no data
    const reportDataSource = !isEmpty(model.dataSource.dataTree.selectedItems)
      ? model.dataSource.dataTree.selectedItems
      : this.model.reportDataSource;
    const selectedDataSourcesCount = !isNil(reportDataSource)
      ? reportDataSource.length
      : defaultSelectedDataSourcesCount;
    const dataSourceLimitValue = AppGlobal.advancedSettings.performance.dataSourceLimitValue;

    if (selectedDataSourcesCount > dataSourceLimitValue) {
      model.updateErrorMessages(Constants.BUNDLE.MESSAGE.M11);
      return Promise.resolve({ isDisableReport: true, success: false });
    } else {
      return Promise.resolve({ success: true });
    }
  }

  /**
   * Get warning message for ignored filters
   * @return {string}
   */
  getUnspecifiedFilterWarning() {
    const reportFilter = getOr({}, 'model.reportFields.reportFilter', this);
    const { logic, filterFields } = reportFilter;
    const firstIndex = 0;
    let message = '';
    if (logic && filterFields) {
      const result = find(
        item => item.state !== Constants.OBJECT_STATE.DELETE && logic.indexOf(item.position) < firstIndex,
        filterFields
      );
      if (result) {
        message = Constants.BUNDLE.MESSAGE.FILTER_WILL_BE_IGNORED;
      }
    }
    return message;
  }

  /**
   * Validate filter logic.
   * @returns {Promise}
   */
  @action
  validateFilterSyntax() {
    const reportFilter = getOr({}, 'model.reportFields.reportFilter', this);
    const { logic, filterFields } = reportFilter;
    const positions =
      filterFields &&
      flow(
        filter(item => item.state !== Constants.OBJECT_STATE.DELETE),
        map(item => item.position)
      )(filterFields);
    return logic ? fetcher.validateFilterSyntax(logic, positions) : Promise.resolve({ success: true });
  }

  /**
   * Update save mode
   * @param {boolean} value
   */
  @action
  setSaveAsMode(value: any) {
    this.saveAsMode = value;
  }

  /**
   * Update isNewReport
   * @param {boolean} value
   */
  @action
  setNewReport(value: any) {
    this.isNewReport = value;
  }

  /**
   * Detect schema change
   * @param {string} pageName report designer page
   */
  @action
  detectSchemaChange(pageName: string) {
    const data = this.model.getReportDefinition(Constants.PAGE_NAME.FIELDS);
    const hasDynamicStore =
      pageName === Constants.PAGE_NAME.DATA_SOURCE
        ? this.model.dataSource.dataTree.hasDynamicStore(true)
        : this.model.reportFields.dataTree.hasDynamicStore(true);

    if (hasDynamicStore) {
      return reportsFetcher.detectSchemaChange(data);
    } else {
      return Promise.resolve({ hasChanged: false });
    }
  }

  @action
  disable() {
    this.disabled = true;
  }

  /**
   * Load report designer definition by its version.
   * @param {integer} version
   */
  @action
  loadReportDesigner(version: any, callback: any, pageId: string, isCancelled: boolean) {
    const reportDesigner = this.model;
    const reportId = reportDesigner.reportId;
    const subReportFilter = reportDesigner.subReportFilter;
    const inheritMaterReportOption = omit(['filters'], subReportFilter);

    !isNil(pageId) && (inheritMaterReportOption.page = pageId);

    let loadReport;

    isCancelled && (inheritMaterReportOption.action = 2);

    if (this.model.draftId) {
      loadReport = reportsFetcher.getPrintDraftData(this.model.draftId);
    } else {
      loadReport = !isNil(version)
        ? reportsFetcher.loadReportVersion(reportId, version, inheritMaterReportOption)
        : fetcher.loadForEdit(reportId, this.isLoadDraft, false).then(function(data) {
            if (!isNil(data.report)) {
              return data.report;
            } else {
              return data;
            }
          });
    }

    return loadReport.then(
      action(function(report) {
        if (!this.isNewReport) {
          if (isNil(report.messages)) {
            reportDesigner.read(report);

            callback && callback();

            // set tenantId for system according to report's tenantID
            if (!isNil(report.tenantId)) {
              // prevent loading new report when changing tenant
              uiState.setTenantId(report.tenantId, true);
            }
          } else {
            this.disableReportDesigner = !isEmpty(report.messages[0].detail);
          }
        }

        this.advancedMode = report.advancedMode;
        this.isCompleteLoad = true;
        return report;
      }).bind(this)
    );
  }

  /**
   * Release all subscribers
   */
  dispose() {
    this.disposers &&
      placeholder(this.disposers).forEach(function(disposer) {
        disposer();
      });
  }

  savePrintDraft() {
    return reportsFetcher.postPrintDraftData(this.model.getReportDefinition());
  }

  /**
   *  Handle validate report
   */
  @action
  validateReport() {
    const data = this.model.getReportDefinition();
    data.ignoreCheckName = true;

    return fetcher.validate(data);
  }

  /**
   * Validate cycle relationship
   */
  @action
  validateCycleRelationships() {
    const data = this.model.getReportRelationship();

    return fetcher.validateCycleRelationships(data);
  }

  /**
   *  Update Result when detech schema change
   */
  @action
  updateResults() {
    const data = this.model.getReportDefinition();

    return fetcher
      .updateResults(data)
      .then(result => {
        // Keep the old report name & preview record
        result.report.name = data.report.name;
        result.report.previewRecord = data.report.previewRecord;

        this.model.updateReportInfo(result.reportKey.key, result.report, true);
        result.success && this.model.read(result.report);

        return result.report;
      })
      .then(report => {
        if (report.dynamicQuerySourceFields.length) {
          this.dataSourceStore.updateDynamicQuerySourceFields();
        }
      });
  }

  /*
   * Reset model
   */
  @action
  resetModel() {
    this.model = new ReportDesigner();
  }

  /**
   * link between store and uiState
   */
  @action
  init() {
    uiState.setCheckDirtyFunction(
      function() {
        const authorization = new SecurityManager();
        const checkPrivileges = authorization.checkPrivileges([PERMISSION_KEY.ReportsActionsOverwriteExistingReport]);
        if (!checkPrivileges || (!this.isNewReport && !this.model.checkSavePermission()))
          return false;
        return this.model.checkDirty();
      }.bind(this),
      this.cancelChanges.bind(this)
    );
  }

  /**
   * unlink between store and uiState
   */
  @action
  destroy() {
    uiState.resetCheckDirtyFunction();
  }

  /**
   * export query excution
   * @param {string} url
   * @param {string} format
   * @param {string} reportId //unused
   * @param {array} filters //unused
   * @param {string} draftId //unused
   * @param {string} queryString //unused
   * @param {object} successCallBack
   * @param {object} failCallBack
   * @returns {Promise}
   */
  exportReport(
    url: any,
    format: any,
    reportId: any,
    filters: any,
    exportObj: any,
    successCallBack: any,
    failCallBack: any
  ) {
    const reportDefinition = this.model.getReportDefinition();

    return exportFetcher.export(
      {
        url: url,
        reportKey: reportDefinition.reportKey.key,
        reportDefinition: JSON.stringify(reportDefinition.report),
        format: format,
        filters: filters,
        draftId: get('draftId', exportObj),
        subReportFilters: get('subReportFilter', exportObj),
        queryString: get('queryString', exportObj),
        tenantId: uiState.getTenantId() // for export API.
      },
      successCallBack,
      failCallBack
    );
  }

  /**
   * Load access default when create new report
   * @returns {Promise}
   */
  loadDefaultAccess() {
    const reportTemplateType = 0;
    return accessFetcher
      .loadDefaultAccess(reportTemplateType)
      .bind(this)
      .then(result => {
        this.model.reportAccess.populateAccessDefault(result);
      });
  }

  /**
   * Update Report Category, used when moving report
   */
  updateReportCategory(category: any, subCategory: any) {
    this.model.updateCategory(get('id', category), get('name', category));
    this.model.updateSubCategory(get('id', subCategory), get('name', subCategory));
  }

  /**
   * Validate report by id
   * @param  {Boolean} isViewer
   */
  @action
  validateReportId(isViewer: any) {
    const reportId = this.model.reportId;
    const model = this.model;
    if (isNil(reportId)) return Promise.resolve(true);

    return fetcher.validateReportIdNoCache(reportId, !isViewer).then(result => {
      if (!result.success) {
        if (isViewer) {
          const messagesResult = result.messages;
          const msg =
            !isNil(messagesResult) &&
            isArray(messagesResult) &&
            messagesResult.length > 0 &&
            !isEmpty(messagesResult[0].key)
              ? messagesResult[0].messages[0]
              : localizer.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_INVALID_REPORT');

          this.alertDetails.set({
            errors: [
              {
                message: msg
              }
            ],
            success: false,
            key: 'report'
          });
        } else {
          if (!isNil(result.messages)) {
            const keys = [
              Constants.ERROR_KEY.calculatedfield,
              Constants.ERROR_KEY.filter,
              Constants.ERROR_KEY.relationship
            ];
            const key = includes(result.messages[0].key, keys) ? 'report' : null;
            const errorMessage = result.messages[0].messages[0];

            // If there is any error when load report => allow user to save change
            if (errorMessage) {
              model.markDirty();
            }

            this.alertDetails.set({
              errors: map(function(message) {
                return { message: message.messages[0] };
              }, result.messages),
              success: false,
              key: key
            });
          }
        }
      }
      return result.success;
    });
  }

  setAccessPriority(accessPriority: any) {
    this.reportAccessPriority = accessPriority;
  }

  /**
   * Update report list uri
   */
  updateReportListURI(uri: string) {
    this.reportListURI = uri;
  }

  /**
   * Reset show/hide report header and footer to default.
   */
  resetShowingHeaderFooter(): void {
    this.isShowingHeaderFooter = !AppGlobal.doesHideReportHeaderAndFooter();
  }
}

export default ReportDesignerStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportDesignerStore.js